import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Progress } from "../components/ui/progress";
import {
  ChevronLeft,
  Package,
  Calendar,
  FileText,
  AlertCircle,
  Star,
  Award,
} from "lucide-react";
import { motion } from "framer-motion";
import { getBatchById, getApprovedReviews } from "../lib/api";
import type { BatchWithProduct, ReviewWithRelations } from "../lib/types";
import { toast } from "sonner";

interface BatchDetailProps {
  batchId?: string;
  onNavigate?: (page: string, id?: string) => void;
}

export function BatchDetail({ batchId, onNavigate }: BatchDetailProps) {
  const [batch, setBatch] = useState<BatchWithProduct | null>(null);
  const [reviews, setReviews] = useState<ReviewWithRelations[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBatchData = async () => {
      if (!batchId) {
        toast.error("Batch ID is required");
        onNavigate && onNavigate("discover");
        return;
      }

      try {
        setLoading(true);
        const [batchData, reviewsData] = await Promise.all([
          getBatchById(batchId),
          getApprovedReviews({ batchId }),
        ]);

        if (!batchData) {
          toast.error("Batch not found");
          onNavigate && onNavigate("discover");
          return;
        }

        setBatch(batchData);
        setReviews(reviewsData);
      } catch (error: any) {
        console.error("Error fetching batch:", error);
        toast.error("Failed to load batch details");
      } finally {
        setLoading(false);
      }
    };

    fetchBatchData();
  }, [batchId, onNavigate]);

  const getGradeFromStars = (stars: number): string => {
    if (stars >= 4.5) return "A+";
    if (stars >= 4.0) return "A";
    if (stars >= 3.5) return "B+";
    if (stars >= 3.0) return "B";
    if (stars >= 2.5) return "C+";
    if (stars >= 2.0) return "C";
    return "D";
  };

  const getGradeColor = (grade: string) => {
    if (grade.startsWith("A")) return "text-primary";
    if (grade.startsWith("B")) return "text-blue-600";
    if (grade.startsWith("C")) return "text-yellow-600";
    return "text-muted-foreground";
  };

  const getGradientClass = (grade: string) => {
    if (grade.startsWith("A")) return "gradient-grade-a";
    if (grade.startsWith("B")) return "gradient-grade-b";
    if (grade.startsWith("C")) return "gradient-grade-c";
    return "bg-muted";
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="max-w-5xl mx-auto">
          <Skeleton className="h-10 w-48 mb-6" />
          <div className="space-y-6">
            <Skeleton className="h-64 w-full rounded-2xl" />
            <Skeleton className="h-96 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!batch) {
    return null;
  }

  // Calculate batch average rating
  const avgStars = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + r.stars, 0) / reviews.length
    : 0;
  const batchGrade = getGradeFromStars(avgStars);

  // Check if batch is expiring soon (within 30 days)
  const expiryDate = batch.expiry_date ? new Date(batch.expiry_date) : null;
  const today = new Date();
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(today.getDate() + 30);
  const isExpiringSoon = expiryDate && expiryDate < thirtyDaysFromNow && expiryDate > today;
  const isExpired = expiryDate && expiryDate < today;

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            variant="ghost"
            className="mb-6 hover:bg-primary/10"
            onClick={() => batch.product && onNavigate && onNavigate("product-detail", batch.product_id)}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Product
          </Button>
        </motion.div>

        {/* Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="p-8 glass-card mb-6">
            <div className="flex flex-col md:flex-row items-start justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Package className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h1 className="text-3xl">Batch #{batch.batch_no}</h1>
                    {batch.product && (
                      <p className="text-lg text-muted-foreground">
                        {batch.product.product_name}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Received Date</p>
                      <p className="font-medium">
                        {batch.received_at
                          ? new Date(batch.received_at).toLocaleDateString()
                          : "Unknown"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Expiry Date</p>
                      <p className="font-medium">
                        {expiryDate ? expiryDate.toLocaleDateString() : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center gap-3">
                <Badge
                  variant={batch.status === "in_stock" ? "default" : "secondary"}
                  className={`${
                    batch.status === "in_stock" ? "gradient-green text-white" : ""
                  } text-sm px-4 py-2`}
                >
                  {batch.status.replace("_", " ").toUpperCase()}
                </Badge>
                {isExpiringSoon && (
                  <Badge variant="outline" className="border-yellow-600 text-yellow-600">
                    Expiring Soon
                  </Badge>
                )}
                {isExpired && (
                  <Badge variant="outline" className="border-destructive text-destructive">
                    Expired
                  </Badge>
                )}
              </div>
            </div>
          </Card>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Information */}
            {batch.product && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                <Card className="p-6 glass-card">
                  <h3 className="text-lg mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Product Details
                  </h3>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                      <p className="text-xs text-muted-foreground mb-1">THC</p>
                      <p className="text-lg font-medium">
                        {batch.product.thc_pct ? `${batch.product.thc_pct}%` : "N/A"}
                      </p>
                    </div>
                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                      <p className="text-xs text-muted-foreground mb-1">CBD</p>
                      <p className="text-lg font-medium">
                        {batch.product.cbd_pct ? `${batch.product.cbd_pct}%` : "N/A"}
                      </p>
                    </div>
                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Form</p>
                      <p className="text-sm font-medium capitalize">
                        {batch.product.form}
                      </p>
                    </div>
                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Pack Size</p>
                      <p className="text-sm font-medium">
                        {batch.product.pack_size || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 p-4 rounded-xl bg-muted/20">
                    <p className="text-sm text-muted-foreground mb-1">Brand</p>
                    <p className="font-medium">{batch.product.brand || "Various Brands"}</p>
                    <p className="text-sm text-muted-foreground mt-2 mb-1">SKU</p>
                    <p className="text-xs font-mono">{batch.product.sku}</p>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Certificate of Analysis */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Card className="p-6 glass-card">
                <h3 className="text-lg mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Certificate of Analysis (COA)
                </h3>

                {batch.coa_url ? (
                  <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                    <p className="text-sm text-muted-foreground mb-3">
                      COA document available for this batch
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a href={batch.coa_url} target="_blank" rel="noopener noreferrer">
                        <FileText className="w-4 h-4 mr-2" />
                        View COA Document
                      </a>
                    </Button>
                  </div>
                ) : (
                  <div className="p-6 rounded-xl bg-muted/30 text-center">
                    <FileText className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      No COA document available for this batch
                    </p>
                  </div>
                )}
              </Card>
            </motion.div>

            {/* Reviews */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <Card className="p-6 glass-card">
                <h3 className="text-lg mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-primary fill-current" />
                  Batch Reviews ({reviews.length})
                </h3>

                {reviews.length === 0 ? (
                  <div className="text-center py-8">
                    <Star className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground mb-4">
                      No reviews for this batch yet
                    </p>
                    <Button
                      onClick={() =>
                        onNavigate && onNavigate("submit-review", batch.product_id)
                      }
                      className="gradient-green hover:opacity-90 transition-opacity"
                    >
                      Write a Review
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div
                        key={review.id}
                        className="p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => onNavigate && onNavigate("review-detail", review.id)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < review.stars
                                      ? "fill-current text-primary"
                                      : "text-muted-foreground"
                                  }`}
                                />
                              ))}
                            </div>
                            <Badge className="text-xs">
                              {getGradeFromStars(review.stars)}
                            </Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(review.submitted_at).toLocaleDateString()}
                          </span>
                        </div>

                        {review.title && (
                          <p className="font-medium mb-1">{review.title}</p>
                        )}
                        {review.body && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {review.body}
                          </p>
                        )}

                        <p className="text-xs text-muted-foreground mt-2">
                          By {review.author?.display_name || "Anonymous"}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Batch Grade */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="p-8 glass-card text-center">
                <div className="mb-4">
                  <Award className="w-8 h-8 mx-auto text-primary" />
                </div>
                <h3 className="text-sm text-muted-foreground mb-4">Batch Grade</h3>
                {reviews.length > 0 ? (
                  <>
                    <div
                      className={`${getGradientClass(
                        batchGrade
                      )} w-32 h-32 rounded-3xl mx-auto flex items-center justify-center shadow-2xl mb-6`}
                    >
                      <span className="text-5xl text-white">{batchGrade}</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-5 h-5 ${
                              i < Math.round(avgStars)
                                ? "fill-current text-primary"
                                : "text-muted-foreground"
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-2xl">{avgStars.toFixed(1)}/5.0</p>
                      <p className="text-sm text-muted-foreground">
                        Based on {reviews.length} {reviews.length === 1 ? "review" : "reviews"}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="py-6">
                    <div className="w-32 h-32 rounded-3xl mx-auto flex items-center justify-center bg-muted/50 mb-4">
                      <span className="text-4xl text-muted-foreground">N/A</span>
                    </div>
                    <p className="text-sm text-muted-foreground">No reviews yet</p>
                  </div>
                )}
              </Card>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <Card className="p-6 glass-card">
                <h3 className="text-lg mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Button
                    className="w-full gradient-green hover:opacity-90 transition-opacity"
                    onClick={() =>
                      onNavigate && onNavigate("submit-review", batch.product_id)
                    }
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Write a Review
                  </Button>
                  {batch.product && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        onNavigate && onNavigate("product-detail", batch.product_id)
                      }
                    >
                      <Package className="w-4 h-4 mr-2" />
                      View Product
                    </Button>
                  )}
                </div>
              </Card>
            </motion.div>

            {/* Disclaimer */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Card className="p-6 glass-card">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm mb-2">Batch Information</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Batch-specific reviews reflect individual experiences with this particular
                      production lot. Quality may vary between batches.
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
